/**
 * Tool result query operations - Get
 */

import type { ChatId, UserId } from '../../types.js';
import { getSupabaseClient } from '../../supabase-client.js';
import type { StoredToolResultSummary, QueryOptions, QueryResult } from '../types.js';
import { logger } from '../../../logger.js';

/**
 * Get a single tool result by ID
 */
export async function getToolResultById(
  toolResultId: string
): Promise<StoredToolResultSummary | null> {
  try {
    const client = getSupabaseClient();
    const { data, error } = await client
      .from('meridian_tool_results')
      .select('*')
      .eq('id', toolResultId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows found
        return null;
      }
      throw error;
    }

    return data as StoredToolResultSummary;
  } catch (error) {
    logger.error('error getting tool result by id', error, { op: 'getToolResultById' });
    throw error;
  }
}

/**
 * Get all tool results for a chat
 */
export async function getToolResultsByChat(
  chatId: ChatId,
  options?: QueryOptions
): Promise<QueryResult<StoredToolResultSummary>> {
  try {
    const client = getSupabaseClient();
    const limit = options?.limit || 50;
    const offset = options?.offset || 0;
    const ascending = options?.ascending ?? false;

    const { data, error, count } = await client
      .from('meridian_tool_results')
      .select('*', { count: 'exact' })
      .eq('chat_id', chatId)
      .order('summarized_at', { ascending })
      .range(offset, offset + limit - 1);

    if (error) {
      throw error;
    }

    const items = (data || []) as StoredToolResultSummary[];
    const hasMore = count ? offset + limit < count : false;

    return {
      items,
      hasMore,
      offset,
    };
  } catch (error) {
    logger.error('error getting tool results by chat', error, { op: 'getToolResultsByChat' });
    throw error;
  }
}

/**
 * Get all tool results for a user
 */
export async function getToolResultsByUser(
  userId: UserId,
  options?: QueryOptions
): Promise<QueryResult<StoredToolResultSummary>> {
  try {
    const client = getSupabaseClient();
    const limit = options?.limit || 50;
    const offset = options?.offset || 0;
    const ascending = options?.ascending ?? false;

    const { data, error, count } = await client
      .from('meridian_tool_results')
      .select('*', { count: 'exact' })
      .eq('user_id', userId)
      .order('summarized_at', { ascending })
      .range(offset, offset + limit - 1);

    if (error) {
      throw error;
    }

    const items = (data || []) as StoredToolResultSummary[];
    const hasMore = count ? offset + limit < count : false;

    return {
      items,
      hasMore,
      offset,
    };
  } catch (error) {
    logger.error('error getting tool results by user', error, { op: 'getToolResultsByUser' });
    throw error;
  }
}

/**
 * Get all tool results for a specific tool
 */
export async function getToolResultsByTool(
  toolName: string,
  options?: QueryOptions
): Promise<QueryResult<StoredToolResultSummary>> {
  try {
    const client = getSupabaseClient();
    const limit = options?.limit || 50;
    const offset = options?.offset || 0;
    const ascending = options?.ascending ?? false;

    const { data, error, count } = await client
      .from('meridian_tool_results')
      .select('*', { count: 'exact' })
      .eq('tool_name', toolName)
      .order('summarized_at', { ascending })
      .range(offset, offset + limit - 1);

    if (error) {
      throw error;
    }

    const items = (data || []) as StoredToolResultSummary[];
    const hasMore = count ? offset + limit < count : false;

    return {
      items,
      hasMore,
      offset,
    };
  } catch (error) {
    logger.error('error getting tool results by tool', error, { op: 'getToolResultsByTool' });
    throw error;
  }
}
